import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Nav, Spinner, Table } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { useAuth } from '../auth';
import {
  adminListPlans, adminUpdatePlan, adminSetPlanStatus, adminCreatePlan,
  adminListFeatures, adminCreateFeature, adminUpdateFeature,
  adminSubscriptionAnalytics, adminActivateForOwner, adminSubscriptionEvents,
  adminSubscriptionOverview, adminApproveActivation, adminRejectActivation, adminGrantDecisionDays,
  adminSendActivationToFinance, financeReviewActivation,
  money
} from '../plansApi';

function useRoleFlags() {
  const { user } = useAuth();
  const has = (r) => user?.role === r || (user?.roles || []).includes(r);
  return {
    isAdmin: has('admin'),
    isFinanceRole: has('finance_employee') || has('finance_lead') || has('finance_manager')
  };
}

// Admin "Subscription" tab (§3–§6, §21, §22): plan pricing & feature matrix, feature catalogue,
// subscription oversight + analytics, and the immutable audit trail. All config is runtime-
// editable with no deploy (§24).
export default function PlansAdminPanel() {
  const { isAdmin, isFinanceRole } = useRoleFlags();
  const [tab, setTab] = useState('overview');

  // membership_approval_flow.md §6 — a finance-team member only reviews pending activation
  // requests; they never see plan config or the audit trail.
  if (isFinanceRole && !isAdmin) return <Subscriptions financeOnly />;

  return (
    <div>
      <Nav variant="pills" className="mb-3" activeKey={tab} onSelect={(k) => setTab(k)}>
        <Nav.Item><Nav.Link eventKey="overview">Overview</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="plans">Plans</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="features">Features</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="subs">Subscriptions</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="audit">Audit</Nav.Link></Nav.Item>
      </Nav>
      {tab === 'overview' && <Overview />}
      {tab === 'plans' && <Plans />}
      {tab === 'features' && <Features />}
      {tab === 'subs' && <Subscriptions />}
      {tab === 'audit' && <Audit />}
    </div>
  );
}

function Overview() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { adminSubscriptionAnalytics().then(setA).catch(() => setErr('Unable to load analytics.')); }, []);
  if (err) return <Alert variant="danger">{err}</Alert>;
  if (!a) return <Spinner animation="border" />;
  const tiles = [
    ['Total owners', a.totalOwners], ['Active subscriptions', a.activeSubscriptions],
    ['Paid', a.paidSubscriptions], ['Free activations', a.freeActivations],
    ['Awaiting approval', a.pendingApproval ?? 0], ['Expiring soon', a.expiring],
    ['Past due', a.pastDue], ['Expired', a.expired],
    ['MRR', money(a.mrr)], ['ARR', money(a.arr)]
  ];
  return (
    <div>
      <div className="row g-2 mb-3">
        {tiles.map(([label, val]) => (
          <div className="col-6 col-md-3" key={label}>
            <Card body className="text-center">
              <div className="fs-4 fw-bold">{val}</div>
              <div className="text-muted small">{label}</div>
            </Card>
          </div>
        ))}
      </div>
      <Card>
        <Card.Header>Plan distribution</Card.Header>
        <Table size="sm" className="mb-0">
          <tbody>
            {Object.entries(a.planDistribution || {}).map(([code, n]) => (
              <tr key={code}><td>{code}</td><td className="text-end">{n}</td></tr>
            ))}
            {Object.keys(a.planDistribution || {}).length === 0 && <tr><td className="text-muted">No active subscriptions yet.</td></tr>}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}

function Plans() {
  const [plans, setPlans] = useState(null);
  const [features, setFeatures] = useState([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    Promise.all([adminListPlans(), adminListFeatures()])
      .then(([p, f]) => { setPlans(p); setFeatures(f); })
      .catch(() => setErr('Unable to load plans.'));
  };
  useEffect(load, []);
  if (err) return <Alert variant="danger">{err}</Alert>;
  if (!plans) return <Spinner animation="border" />;

  const quickPatch = async (id, patch) => {
    setErr(''); setMsg('');
    try { await adminUpdatePlan(id, patch); setMsg('Saved.'); load(); }
    catch (e) { setErr(e.response?.data?.msg || 'Save failed.'); }
  };
  const toggleStatus = async (p) => {
    try { await adminSetPlanStatus(p.id, p.status === 'active' ? 'disabled' : 'active'); load(); }
    catch (e) { setErr(e.response?.data?.msg || 'Failed.'); }
  };

  return (
    <div>
      {msg && <Alert variant="success" onClose={() => setMsg('')} dismissible>{msg}</Alert>}
      {err && <Alert variant="danger" onClose={() => setErr('')} dismissible>{err}</Alert>}
      <div className="d-flex justify-content-end mb-2">
        <Button size="sm" onClick={() => setCreating(true)}>+ New plan</Button>
      </div>
      <div className="table-responsive">
        <Table bordered size="sm" className="align-middle">
          <thead><tr><th>Plan</th><th>Monthly</th><th>Yearly</th><th>Visible</th><th>Popular</th><th>Free activation</th><th>v</th><th></th></tr></thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className={p.status !== 'active' ? 'text-muted' : ''}>
                <td>{p.name}<div className="small text-muted">{p.code}</div></td>
                <td style={{ width: 110 }}>
                  <Form.Control size="sm" type="number" defaultValue={p.monthlyPrice}
                    onBlur={(e) => Number(e.target.value) !== p.monthlyPrice && quickPatch(p.id, { monthlyPrice: Number(e.target.value) })} />
                </td>
                <td style={{ width: 110 }}>
                  <Form.Control size="sm" type="number" defaultValue={p.yearlyPrice}
                    onBlur={(e) => Number(e.target.value) !== p.yearlyPrice && quickPatch(p.id, { yearlyPrice: Number(e.target.value) })} />
                </td>
                <td><Form.Check type="switch" defaultChecked={p.visible !== false}
                  onChange={(e) => quickPatch(p.id, { visible: e.target.checked })} /></td>
                <td><Form.Check type="switch" defaultChecked={p.recommended}
                  onChange={(e) => quickPatch(p.id, { recommended: e.target.checked })} /></td>
                <td>
                  <Form.Check type="switch" label={p.freeActivationConfig?.enabled ? `${p.freeActivationConfig.durationDays}d` : 'off'}
                    defaultChecked={!!p.freeActivationConfig?.enabled}
                    onChange={(e) => quickPatch(p.id, { freeActivation: { enabled: e.target.checked, durationDays: p.freeActivationConfig?.durationDays || 90 } })} />
                </td>
                <td>{p.version}</td>
                <td className="text-nowrap">
                  <Button size="sm" variant="outline-secondary" onClick={() => setEditing(p)}>Features</Button>{' '}
                  <Button size="sm" variant={p.status === 'active' ? 'outline-danger' : 'outline-success'} onClick={() => toggleStatus(p)}>
                    {p.status === 'active' ? 'Disable' : 'Enable'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {editing && (
        <PlanFeaturesModal plan={editing} features={features} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {creating && (
        <NewPlanModal features={features} onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function PlanFeaturesModal({ plan, features, onClose, onSaved }) {
  const initial = {};
  features.forEach((f) => {
    const entry = plan.features.find((e) => e.featureCode === f.featureCode);
    initial[f.featureCode] = entry ? entry.value : (f.valueType === 'QUANTITY' ? 0 : false);
  });
  const [vals, setVals] = useState(initial);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true); setErr('');
    const list = features
      .filter((f) => {
        const v = vals[f.featureCode];
        return f.valueType === 'QUANTITY' ? (v === 'UNLIMITED' || Number(v) > 0) : v === true;
      })
      .map((f) => ({ featureCode: f.featureCode, value: f.valueType === 'QUANTITY'
        ? (vals[f.featureCode] === 'UNLIMITED' ? 'UNLIMITED' : Number(vals[f.featureCode])) : true }));
    try { await adminUpdatePlan(plan.id, { features: list }); onSaved(); }
    catch (e) { setErr(e.response?.data?.msg || 'Save failed.'); setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton><Modal.Title>{plan.name} — features & limits</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <p className="text-muted small">Editing prices or features bumps the plan version — live subscriptions keep the version they bought.</p>
        <Table size="sm">
          <tbody>
            {features.map((f) => (
              <tr key={f.featureCode}>
                <td>{f.name}<div className="small text-muted">{f.category}</div></td>
                <td style={{ width: 160 }}>
                  {f.valueType === 'BOOLEAN' ? (
                    <Form.Check type="switch" checked={vals[f.featureCode] === true}
                      onChange={(e) => setVals({ ...vals, [f.featureCode]: e.target.checked })} />
                  ) : (
                    <div className="d-flex gap-1 align-items-center">
                      <Form.Control size="sm" type="number" style={{ width: 80 }}
                        value={vals[f.featureCode] === 'UNLIMITED' ? '' : vals[f.featureCode]}
                        disabled={vals[f.featureCode] === 'UNLIMITED'}
                        onChange={(e) => setVals({ ...vals, [f.featureCode]: e.target.value })} />
                      <Form.Check type="checkbox" label="∞" checked={vals[f.featureCode] === 'UNLIMITED'}
                        onChange={(e) => setVals({ ...vals, [f.featureCode]: e.target.checked ? 'UNLIMITED' : 0 })} />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function NewPlanModal({ features, onClose, onCreated }) {
  const [form, setForm] = useState({ code: '', name: '', tagline: '', monthlyPrice: 0, yearlyPrice: 0 });
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async () => {
    setErr('');
    try {
      await adminCreatePlan({
        code: form.code.toUpperCase(), name: form.name, tagline: form.tagline,
        monthlyPrice: Number(form.monthlyPrice), yearlyPrice: Number(form.yearlyPrice), features: []
      });
      onCreated();
    } catch (e) { setErr(e.response?.data?.msg || 'Create failed.'); }
  };
  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton><Modal.Title>New plan</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Form.Group className="mb-2"><Form.Label>Code</Form.Label><Form.Control value={form.code} onChange={set('code')} placeholder="STARTER" /></Form.Group>
        <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={set('name')} /></Form.Group>
        <Form.Group className="mb-2"><Form.Label>Tagline</Form.Label><Form.Control value={form.tagline} onChange={set('tagline')} /></Form.Group>
        <div className="row">
          <div className="col"><Form.Label>Monthly ₹</Form.Label><Form.Control type="number" value={form.monthlyPrice} onChange={set('monthlyPrice')} /></div>
          <div className="col"><Form.Label>Yearly ₹</Form.Label><Form.Control type="number" value={form.yearlyPrice} onChange={set('yearlyPrice')} /></div>
        </div>
        <p className="text-muted small mt-2">Add features after creating, from the plan's Features button.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!form.code || !form.name} onClick={create}>Create</Button>
      </Modal.Footer>
    </Modal>
  );
}

function Features() {
  const [features, setFeatures] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({ featureCode: '', name: '', category: 'General', valueType: 'BOOLEAN' });
  // Block body (not an expression arrow) so the effect returns undefined, not the promise —
  // a returned promise gets invoked as a cleanup function on unmount ("destroy is not a function").
  const load = () => { adminListFeatures().then(setFeatures).catch(() => setErr('Unable to load features.')); };
  useEffect(load, []);
  if (err) return <Alert variant="danger">{err}</Alert>;
  if (!features) return <Spinner animation="border" />;

  const add = async () => {
    setErr('');
    try {
      await adminCreateFeature({ ...form, featureCode: form.featureCode.toUpperCase() });
      setForm({ featureCode: '', name: '', category: 'General', valueType: 'BOOLEAN' }); load();
    } catch (e) { setErr(e.response?.data?.msg || 'Create failed.'); }
  };
  const toggle = async (f) => {
    try { await adminUpdateFeature(f._id, { status: f.status === 'active' ? 'disabled' : 'active' }); load(); }
    catch (e) { setErr(e.response?.data?.msg || 'Failed.'); }
  };

  return (
    <div>
      <Table bordered size="sm">
        <thead><tr><th>Code</th><th>Name</th><th>Category</th><th>Type</th><th>Core</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {features.map((f) => (
            <tr key={f._id}>
              <td className="small">{f.featureCode}</td><td>{f.name}</td><td>{f.category}</td>
              <td>{f.valueType}{f.valueType === 'QUANTITY' && f.unit ? ` (${f.unit})` : ''}</td>
              <td>{f.core ? <Badge bg="secondary">core</Badge> : ''}</td>
              <td><Badge bg={f.status === 'active' ? 'success' : 'secondary'}>{f.status}</Badge></td>
              <td><Button size="sm" variant="outline-secondary" onClick={() => toggle(f)}>{f.status === 'active' ? 'Disable' : 'Enable'}</Button></td>
            </tr>
          ))}
          <tr>
            <td><Form.Control size="sm" placeholder="CODE" value={form.featureCode} onChange={(e) => setForm({ ...form, featureCode: e.target.value })} /></td>
            <td><Form.Control size="sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></td>
            <td><Form.Control size="sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></td>
            <td>
              <Form.Select size="sm" value={form.valueType} onChange={(e) => setForm({ ...form, valueType: e.target.value })}>
                <option>BOOLEAN</option><option>QUANTITY</option>
              </Form.Select>
            </td>
            <td colSpan={2}></td>
            <td><Button size="sm" disabled={!form.featureCode || !form.name} onClick={add}>Add</Button></td>
          </tr>
        </tbody>
      </Table>
      {err && <Alert variant="danger">{err}</Alert>}
    </div>
  );
}

// membership_approval_flow.md §4 — the website admin's one page: what payments are due (pending
// approval requests + past-due), whose plan is expiring, and who is subscribed. A finance-team
// member sees the same page but can only add an advisory review (`financeOnly`).
function Subscriptions({ financeOnly = false }) {
  const { isAdmin } = useRoleFlags();
  const canDecide = isAdmin && !financeOnly;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState(null); // { kind: 'reject'|'finance'|'review', row }
  const [decision, setDecision] = useState({ ownerId: '', days: 7, reason: '' });
  const [grant, setGrant] = useState({ ownerId: '', planCode: 'PROFESSIONAL', mode: 'free', durationDays: 90, paymentReference: '' });

  const load = () => { adminSubscriptionOverview().then(setData).catch(() => setErr('Unable to load the membership overview.')); };
  useEffect(load, []);

  const act = async (fn, okMsg) => {
    setErr(''); setMsg('');
    try { await fn(); setMsg(okMsg); setModal(null); load(); }
    catch (e) { setErr(e.response?.data?.msg || 'That action failed.'); }
  };
  const approve = (row) => act(() => adminApproveActivation(row.id), 'Membership activated.');
  const doGrant = () => act(() => adminActivateForOwner(grant), 'Subscription activated for that owner.');

  const duePaged = usePagedArray(data?.due || []);
  const expiringPaged = usePagedArray(data?.expiring || []);
  const subscribedPaged = usePagedArray(data?.subscribed || []);

  if (err && !data) return <Alert variant="danger">{err}</Alert>;
  if (!data) return <Spinner animation="border" />;

  const due = data.due || [];

  return (
    <div>
      {msg && <Alert variant="success" onClose={() => setMsg('')} dismissible>{msg}</Alert>}
      {err && <Alert variant="danger" onClose={() => setErr('')} dismissible>{err}</Alert>}

      {/* ---- Due payments & approvals ---- */}
      <Card className="mb-4">
        <Card.Header className="fw-semibold">Due payments &amp; approvals ({due.length})</Card.Header>
        <div className="table-responsive">
          <Table hover size="sm" className="mb-0 align-middle">
            <thead><tr>
              <th>Member</th><th>Plan</th><th>Amount</th><th>Payment</th><th>Submitted</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {due.length === 0 && <tr><td colSpan={7} className="text-muted">Nothing due.</td></tr>}
              {duePaged.rows.map((r) => {
                const a = r.approvalRequest;
                return (
                  <tr key={r.id}>
                    <td>{r.ownerName || r.ownerId}<div className="small text-muted">{r.ownerEmail}</div></td>
                    <td>{r.planName || r.planCode}<div className="small text-muted">{(r.billingCycle || '').toLowerCase()}</div></td>
                    <td>{money(a?.amount || r.price, a?.currency || r.currency)}</td>
                    <td className="small">
                      {a
                        ? <>{String(a.paymentMethod || '').replace('_', ' ').toLowerCase()}
                            {a.claimedReference ? <div className="text-muted">ref {a.claimedReference}</div> : null}
                            {a.claimedPaidAt ? <div className="text-muted">paid {new Date(a.claimedPaidAt).toLocaleDateString('en-IN')}</div> : null}
                            {a.ownerNote ? <div className="text-muted fst-italic">“{a.ownerNote}”</div> : null}</>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="small">{a?.requestedAt ? new Date(a.requestedAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td>
                      <Badge bg={r.kind === 'APPROVAL' ? 'warning' : 'danger'} text={r.kind === 'APPROVAL' ? 'dark' : undefined}>
                        {r.status.replace('_', ' ')}
                      </Badge>
                      {a?.financeReview?.recommendation && (
                        <div className="small text-muted mt-1">finance: {a.financeReview.recommendation}</div>
                      )}
                    </td>
                    <td className="text-nowrap">
                      {r.kind === 'APPROVAL' && canDecide && (
                        <>
                          <Button size="sm" variant="success" onClick={() => approve(r)}>Approve &amp; activate</Button>{' '}
                          <Button size="sm" variant="outline-danger" onClick={() => setModal({ kind: 'reject', row: r })}>Reject</Button>{' '}
                          <Button size="sm" variant="outline-secondary" onClick={() => setModal({ kind: 'finance', row: r })}>Send to finance</Button>
                        </>
                      )}
                      {r.kind === 'APPROVAL' && financeOnly && (
                        <Button size="sm" variant="outline-primary" onClick={() => setModal({ kind: 'review', row: r })}>Add review</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Pager {...duePaged} className="px-2 pb-2" />
        </div>
      </Card>

      {/* ---- Expiring soon ---- */}
      <Card className="mb-4">
        <Card.Header className="fw-semibold">Expiring within {data.expiringWindowDays} days ({data.expiring.length})</Card.Header>
        <div className="table-responsive">
          <Table size="sm" className="mb-0">
            <thead><tr><th>Member</th><th>Plan</th><th>Ends</th><th>Days left</th><th>Auto-renew</th></tr></thead>
            <tbody>
              {data.expiring.length === 0 && <tr><td colSpan={5} className="text-muted">None.</td></tr>}
              {expiringPaged.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.ownerName || r.ownerId}<div className="small text-muted">{r.ownerEmail}</div></td>
                  <td>{r.planName || r.planCode}</td>
                  <td>{r.endDate ? new Date(r.endDate).toLocaleDateString('en-IN') : '—'}</td>
                  <td>{r.daysLeft}</td>
                  <td>{r.autoRenew ? 'On' : 'Off'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pager {...expiringPaged} className="px-2 pb-2" />
        </div>
      </Card>

      {/* ---- Subscribed members ---- */}
      <Card className="mb-4">
        <Card.Header className="fw-semibold">Subscribed members ({data.subscribed.length})</Card.Header>
        <div className="table-responsive">
          <Table size="sm" className="mb-0">
            <thead><tr><th>Member</th><th>Plan</th><th>Status</th><th>Cycle</th><th>Price</th><th>Renews / ends</th></tr></thead>
            <tbody>
              {data.subscribed.length === 0 && <tr><td colSpan={6} className="text-muted">No active memberships.</td></tr>}
              {subscribedPaged.rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.ownerName || r.ownerId}<div className="small text-muted">{r.ownerEmail}</div></td>
                  <td>{r.planName || r.planCode}</td>
                  <td><Badge bg="secondary">{r.status.replace('_', ' ')}</Badge></td>
                  <td>{(r.billingCycle || '').toLowerCase()}</td>
                  <td>{r.price ? money(r.price, r.currency) : 'Free'}</td>
                  <td>{r.endDate ? new Date(r.endDate).toLocaleDateString('en-IN') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pager {...subscribedPaged} className="px-2 pb-2" />
        </div>
      </Card>

      {canDecide && (
        <Card className="mb-3"><Card.Body>
          <div className="fw-semibold mb-2">Grant a plan to an owner directly</div>
          <div className="row g-2 align-items-end">
            <div className="col-md-3"><Form.Label className="small">Owner ID</Form.Label>
              <Form.Control size="sm" value={grant.ownerId} onChange={(e) => setGrant({ ...grant, ownerId: e.target.value })} /></div>
            <div className="col-md-2"><Form.Label className="small">Plan</Form.Label>
              <Form.Control size="sm" value={grant.planCode} onChange={(e) => setGrant({ ...grant, planCode: e.target.value.toUpperCase() })} /></div>
            <div className="col-md-2"><Form.Label className="small">Mode</Form.Label>
              <Form.Select size="sm" value={grant.mode} onChange={(e) => setGrant({ ...grant, mode: e.target.value })}>
                <option value="free">Free</option><option value="paid">Paid (record)</option>
              </Form.Select></div>
            {grant.mode === 'free'
              ? <div className="col-md-2"><Form.Label className="small">Days</Form.Label>
                  <Form.Control size="sm" type="number" value={grant.durationDays} onChange={(e) => setGrant({ ...grant, durationDays: Number(e.target.value) })} /></div>
              : <div className="col-md-3"><Form.Label className="small">Payment ref</Form.Label>
                  <Form.Control size="sm" value={grant.paymentReference} onChange={(e) => setGrant({ ...grant, paymentReference: e.target.value })} /></div>}
            <div className="col-md-2"><Button size="sm" disabled={!grant.ownerId} onClick={doGrant}>Activate</Button></div>
          </div>
        </Card.Body></Card>
      )}

      {canDecide && <Card className="mb-3"><Card.Body>
        <div className="fw-semibold mb-2">Grant free decision days</div>
        <p className="small text-muted">Temporarily restore this owner's listings and new bookings without recording a payment. Existing orders remain accessible after the period ends.</p>
        <div className="row g-2 align-items-end">
          <div className="col-md-4"><Form.Label>Owner ID</Form.Label><Form.Control aria-label="Decision days owner ID" value={decision.ownerId} onChange={(e) => setDecision({ ...decision, ownerId: e.target.value })} /></div>
          <div className="col-md-2"><Form.Label>Days (1?30)</Form.Label><Form.Control aria-label="Free decision days" type="number" min={1} max={30} value={decision.days} onChange={(e) => setDecision({ ...decision, days: Number(e.target.value) })} /></div>
          <div className="col-md-4"><Form.Label>Reason</Form.Label><Form.Control aria-label="Decision days reason" maxLength={500} value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} /></div>
          <div className="col-md-2"><Button disabled={!decision.ownerId || !decision.reason.trim() || !Number.isInteger(decision.days) || decision.days < 1 || decision.days > 30} onClick={() => act(() => adminGrantDecisionDays(decision.ownerId, decision.days, decision.reason), 'Free decision days granted. Listings can accept bookings again.')}>Grant days</Button></div>
        </div>
      </Card.Body></Card>}

      {modal && <DecisionModal modal={modal} onClose={() => setModal(null)} onDone={(fn, m) => act(fn, m)} />}
    </div>
  );
}

function DecisionModal({ modal, onClose, onDone }) {
  const { row, kind } = modal;
  const [text, setText] = useState('');
  const [rec, setRec] = useState('APPROVE');
  const titles = { reject: 'Reject activation request', finance: 'Send to finance', review: 'Add finance review' };

  const submit = () => {
    if (kind === 'reject') return onDone(() => adminRejectActivation(row.id, text), 'Request rejected.');
    if (kind === 'finance') return onDone(() => adminSendActivationToFinance(row.id, text), 'Sent to finance for review.');
    return onDone(() => financeReviewActivation(row.id, { recommendation: rec, note: text }), 'Review recorded.');
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton><Modal.Title>{titles[kind]}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="text-muted small">{row.ownerName || row.ownerEmail} — {row.planName || row.planCode}</p>
        {kind === 'review' && (
          <Form.Group className="mb-2">
            <Form.Label className="small">Recommendation</Form.Label>
            <Form.Select size="sm" value={rec} onChange={(e) => setRec(e.target.value)}>
              <option value="APPROVE">Approve — payment confirmed</option>
              <option value="HOLD">Hold — need more information</option>
              <option value="REJECT">Reject — payment not found</option>
            </Form.Select>
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label className="small">{kind === 'reject' ? 'Reason' : 'Note'}{kind === 'reject' ? '' : ' (optional)'}</Form.Label>
          <Form.Control as="textarea" rows={3} size="sm" value={text} onChange={(e) => setText(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant={kind === 'reject' ? 'danger' : 'primary'} disabled={kind === 'reject' && !text.trim()} onClick={submit}>
          {kind === 'reject' ? 'Reject' : kind === 'finance' ? 'Send' : 'Save review'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function Audit() {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { adminSubscriptionEvents().then(setEvents).catch(() => setErr('Unable to load the audit trail.')); }, []);
  const paged = usePagedArray(events || []);
  if (err) return <Alert variant="danger">{err}</Alert>;
  if (!events) return <Spinner animation="border" />;
  return (
    <div className="table-responsive">
      <Table bordered size="sm">
        <thead><tr><th>When</th><th>Entity</th><th>Ref</th><th>Action</th><th>Actor</th></tr></thead>
        <tbody>
          {paged.rows.map((e) => (
            <tr key={e.id}>
              <td className="small text-nowrap">{new Date(e.at).toLocaleString('en-IN')}</td>
              <td>{e.entity}</td>
              <td className="small">{e.entityRef}</td>
              <td>{e.action.replace(/_/g, ' ')}</td>
              <td className="small">{e.actorRole || '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pager {...paged} />
    </div>
  );
}
